import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../Auth/AuthContext';
import { 
  X, 
  Heart, 
  ChevronLeft, 
  ChevronRight, 
  MessageCircle, 
  Send, 
  Clock, 
  Car, 
  Users, 
  Navigation,
  Cloud,
  Share2,
  MapPin,
  ExternalLink,
  Camera,
  Facebook,
  Twitter,
  Mail,
  Copy,
  MapIcon,
  Route,
  Expand,
  Check,
  Bookmark,
  Sun,
  Footprints
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { UserMention } from '../ui/UserLink';
import ClickableTag from '../ui/ClickableTag';
import { ClickableMetadataBadge } from '../ui/ClickableMetadataBadge';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import useUserInteractions from '../../hooks/useUserInteractions';
import AddVisitModal from './AddVisitModal';
import AddToWishlistButton from '../ui/AddToWishlistButton';
import CommentsSection from '../Comments/CommentsSection';
import { trackSpotView, trackSpotLike, trackSpotComment, trackSpotShare } from '../../services/tracking';
import {
  WEATHER_OPTIONS, LIGHT_OPTIONS, CROWD_LEVEL_OPTIONS,
  DIFFICULTY_OPTIONS, TIME_WINDOW_OPTIONS
} from '../../constants/spotEnums';


function SpotDetailsModal({ onClose }) {
  const { state, actions } = useApp();
  const { isAuthenticated } = useAuth();
  const { isSpotLiked, isSpotVisited, getVisitCount, toggleLike, addVisit } = useUserInteractions();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showParkingPhoto, setShowParkingPhoto] = useState(false);
  const [showParkingMap, setShowParkingMap] = useState(false);
  const [showParkingPhotos, setShowParkingPhotos] = useState(false);
  const [currentParkingImageIndex, setCurrentParkingImageIndex] = useState(0);
  const [showGpsOptions, setShowGpsOptions] = useState(false);
  const [showFullScreenMap, setShowFullScreenMap] = useState(false);
  const [showAddVisitModal, setShowAddVisitModal] = useState(false);

  const spot = state.selectedSpot;

  // Get parking photos from either structure (v2 uses parking.parking_photos)
  const parkingPhotos = spot?.parking?.parking_photos || spot?.parking_photos || [];
  
  // Helper to get image URL (supports both string and object formats)
  const getParkingPhotoUrl = (photo) => {
    if (typeof photo === 'string') return photo;
    return photo?.url || photo?.cdn_url || '';
  };

  // Track spot view when modal opens
  useEffect(() => {
    if (spot?.id) {
      trackSpotView(spot.id, spot.title);
    }
  }, [spot?.id]);

  // Function to open Google Maps
  const openGoogleMaps = (lat, lon, destination) => {
    const mapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&amp;ll=`;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLon = position.coords.longitude;
          const urlWithOrigin = `https://www.google.com/maps/dir/${userLat},${userLon}/${lat},${lon}`;
          window.open(urlWithOrigin, '_blank');
        },
        () => {
          // Fallback without user location
          const fallbackUrl = `https://www.google.com/maps?q=${lat},${lon}`;
          window.open(fallbackUrl, '_blank');
        }
      );
    } else {
      const fallbackUrl = `https://www.google.com/maps?q=${lat},${lon}`;
      window.open(fallbackUrl, '_blank');
    }
  };

  // Check if parking coordinates are available
  const hasValidParkingCoords = () => {
    return spot.parking_latitude && spot.parking_longitude && 
           spot.practical_info?.parking_distance && 
           !spot.practical_info.parking_distance.toLowerCase().includes('pas de') && 
           !spot.practical_info.parking_distance.toLowerCase().includes('aucun');
  };

  // Function to calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // metres
    const φ1 = lat1 * Math.PI/180; // φ, λ in radians
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // in metres
  };

  // Function to navigate parking photos
  const navigateParkingImage = (direction) => {
    const total = parkingPhotos?.length || 0;
    if (total <= 1) return;
    
    if (direction === 'next') {
      setCurrentParkingImageIndex((currentParkingImageIndex + 1) % total);
    } else {
      setCurrentParkingImageIndex((currentParkingImageIndex - 1 + total) % total);
    }
  };

  // Create custom parking emoji marker
  const createParkingIcon = () => {
    return L.divIcon({
      html: '<div style="background: white; border: 2px solid #16a34a; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-size: 16px; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">🅿️</div>',
      className: 'custom-parking-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
  };

  useEffect(() => {
    if (spot && spot.id) {
      actions.fetchComments(spot.id);
    }
  }, [spot, actions]);

  // Focus management for parking photos modal
  useEffect(() => {
    if (showParkingPhotos) {
      // Focus the parking photos modal to capture keyboard events
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          setShowParkingPhotos(false);
        }
      };
      
      document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [showParkingPhotos]);

  if (!spot) return null;

  const handleClose = () => {
    actions.setShowSpotDetails(false);
    if (onClose) onClose();
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      handleClose();
      navigate('/login');
      return;
    }
    
    try {
      await toggleLike(spot.id);
    } catch (error) {
      console.error('Error liking spot:', error);
      alert('Erreur lors du like: ' + error.message);
    }
  };

  const handleVisitClick = () => {
    if (!isAuthenticated) {
      handleClose();
      navigate('/login');
      return;
    }
    
    setShowAddVisitModal(true);
  };

  const handleAddVisit = async (visitData) => {
    try {
      await addVisit(spot.id, visitData);
    } catch (error) {
      console.error('Error adding visit:', error);
      throw error;
    }
  };

  const handleAddComment = async () => {
    if (!state.newComment.trim()) return;
    
    try {
      const commentData = {
        author: state.currentUser?.username || 'anonyme',
        comment: state.newComment.trim(),
        date: new Date().toISOString()
      };
      
      await actions.addComment(spot.id, commentData);
      actions.setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleShare = (platform) => {
    const spotUrl = `${window.location.origin}/spot/${spot.id}?details=true`;
    const shareText = `Découvrez ce magnifique spot : ${spot.title} - ${spot.description.substring(0, 100)}...`;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(spotUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(spotUrl)}&text=${encodeURIComponent(shareText)}&hashtags=SunSeat,Nature,Detente`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + spotUrl)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent('Découvrez ce spot sur Sun-Spot')}&body=${encodeURIComponent(shareText + '\n\nVoir le spot : ' + spotUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(spotUrl).then(() => {
          alert('Lien copié dans le presse-papier !');
        }).catch(err => {
          console.error('Erreur lors de la copie:', err);
          const textArea = document.createElement('textarea');
          textArea.value = spotUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Lien copié dans le presse-papier !');
        });
        break;
      default:
        break;
    }
  };

  const handleImageClick = (imageIndex, e) => {
    if (e) e.stopPropagation();
    
    // Use TikTok Gallery instead of lightbox for consistency
    actions.openSpotGallery(spot, imageIndex);
  };

  const navigateImage = (direction) => {
    const total = spot.photos?.length || 0;
    if (total <= 1) return;
    
    if (direction === 'next') {
      setCurrentImageIndex((currentImageIndex + 1) % total);
    } else {
      setCurrentImageIndex((currentImageIndex - 1 + total) % total);
    }
  };

  const getOccupationColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'faible': return 'text-green-600';
      case 'modéré': case 'modérée': return 'text-yellow-600';
      case 'élevé': case 'élevée': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getAccessibilityIcon = (accessibility) => {
    if (accessibility?.toLowerCase().includes('voiture')) return '🚗';
    if (accessibility?.toLowerCase().includes('marche')) return '🚶';
    if (accessibility?.toLowerCase().includes('vélo')) return '🚴';
    return '🚶';
  };

  const isLiked = isAuthenticated ? isSpotLiked(spot.id) : false;
  const isVisited = isAuthenticated ? isSpotVisited(spot.id) : false;
  const visitCount = isAuthenticated ? getVisitCount(spot.id) : 0;
  const currentPhoto = spot.photos?.[currentImageIndex];

  return (
    <>
      <Dialog open={state.showSpotDetails} onOpenChange={showParkingPhotos ? () => {} : handleClose}>
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          hideOverlay={state.showLightbox || showParkingPhotos}
        >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{spot.title}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Images */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            {spot.photos && spot.photos.length > 0 && (
              <div className="relative">
                <div className="relative h-64 bg-gray-100 rounded-lg overflow-hidden group">
                  <img
                    src={currentPhoto?.url || currentPhoto}
                    alt={currentPhoto?.metadata?.title || spot.title}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={(e) => handleImageClick(currentImageIndex, e)}
                  />
                  
                  {/* Expand overlay on hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                    <Expand className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={32} />
                  </div>

                  {/* Navigation arrows */}
                  {spot.photos.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateImage('prev')}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70"
                      >
                        <ChevronLeft size={20} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateImage('next')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70"
                      >
                        <ChevronRight size={20} />
                      </Button>
                    </>
                  )}

                  {/* Image counter */}
                  {spot.photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                      {currentImageIndex + 1} / {spot.photos.length}
                    </div>
                  )}
                </div>

                {/* Photo metadata and author */}
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm space-y-1">
                    {/* Show metadata only if available */}
                    {currentPhoto?.metadata && (
                      <>
                        {currentPhoto.metadata.title && (
                          <div className="font-medium">{currentPhoto.metadata.title}</div>
                        )}
                        <div className="text-xs text-gray-600 grid grid-cols-2 gap-1 mb-2">
                          {currentPhoto.metadata.date && <span>📅 {currentPhoto.metadata.date}</span>}
                          {currentPhoto.metadata.time && <span>🕐 {currentPhoto.metadata.time}</span>}
                          {currentPhoto.metadata.season && <span>🌸 {currentPhoto.metadata.season}</span>}
                          {currentPhoto.metadata.weather && <span>🌤️ {currentPhoto.metadata.weather}</span>}
                        </div>
                      </>
                    )}
                    
                    {/* Photo Author - always show */}
                    <div className={`text-sm text-gray-700 font-medium ${currentPhoto?.metadata ? 'border-t pt-2' : ''}`}>
                      Publié par <UserMention username={currentPhoto?.author || spot.author || 'anonyme'} size="sm" />
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons - always show under photo info */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/* Like Button */}
                  <Button
                    variant={isLiked ? "default" : "ghost"}
                    onClick={handleLike}
                    className={`flex items-center space-x-2 ${
                      isLiked ? 'bg-red-500 hover:bg-red-600 text-white' : 'hover:bg-red-50 hover:text-red-600'
                    }`}
                  >
                    <Heart size={16} className={isLiked ? 'fill-current' : ''} />
                    <span>{spot.likes || 0} J&apos;aime</span>
                  </Button>

                  {/* Visited Button */}
                  <Button
                    variant={isVisited ? "default" : "ghost"}
                    onClick={handleVisitClick}
                    className={`flex items-center space-x-2 ${
                      isVisited 
                        ? 'bg-green-500 hover:bg-green-600 text-white' 
                        : 'hover:bg-green-50 hover:text-green-600'
                    }`}
                    title={isVisited ? `Visité ${visitCount} fois` : 'Marquer comme visité'}
                  >
                    <Check size={16} className={isVisited ? 'fill-current' : ''} />
                    <span>
                      {isVisited ? `Visité${visitCount > 1 ? ` (${visitCount})` : ''}` : 'Visité'}
                    </span>
                  </Button>

                  {/* Wishlist Button */}
                  <div onClick={(e) => {
                    if (!isAuthenticated) {
                      e.stopPropagation();
                      handleClose();
                      navigate('/login');
                    }
                  }}>
                    <AddToWishlistButton 
                      spot={spot}
                      size="sm"
                      variant="ghost"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Comments Section - Moved to Left Column */}
            <CommentsSection 
              spotId={spot.id} 
              comments={spot.comments?.length > 0 ? spot.comments : state.comments}
              onCommentAdded={(newComment) => {
                trackSpotComment(spot.id);
              }}
            />

          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Description */}
            <div>
              <p className="text-gray-700 leading-relaxed">{spot.description}</p>
            </div>

            {/* Practical Info */}
            {(spot.v2_data || spot.practical_info) && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Informations pratiques</h3>
                <p className="text-xs text-gray-500 mb-3">Cliquez sur un badge pour filtrer les spots similaires</p>
                <div className="space-y-4">
                  {/* V2 Data - Créneaux horaires */}
                  {spot.v2_data?.recommended_time_windows?.length > 0 && (
                    <div className="flex items-start space-x-2">
                      <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Meilleurs moments</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {spot.v2_data.recommended_time_windows.map((tw) => (
                            <ClickableMetadataBadge 
                              key={tw.type}
                              category="time_windows"
                              value={tw.type}
                              size="sm"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* V2 Data - Conditions météo idéales */}
                  {spot.v2_data?.ideal_conditions?.weather?.length > 0 && (
                    <div className="flex items-start space-x-2">
                      <Cloud className="w-5 h-5 text-sky-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Météo idéale</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {spot.v2_data.ideal_conditions.weather.map((w) => (
                            <ClickableMetadataBadge 
                              key={w}
                              category="weather"
                              value={w}
                              size="sm"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* V2 Data - Lumière idéale */}
                  {spot.v2_data?.ideal_conditions?.light?.length > 0 && (
                    <div className="flex items-start space-x-2">
                      <Sun className="w-5 h-5 text-orange-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Lumière idéale</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {spot.v2_data.ideal_conditions.light.map((l) => (
                            <ClickableMetadataBadge 
                              key={l}
                              category="light"
                              value={l}
                              size="sm"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* V2 Data - Affluence */}
                  {spot.v2_data?.crowd_level && (
                    <div className="flex items-start space-x-2">
                      <Users className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Affluence</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          <ClickableMetadataBadge 
                            category="crowd_level"
                            value={spot.v2_data.crowd_level}
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* V2 Data - Accessibilité */}
                  {spot.v2_data?.accessibility && (
                    <div className="flex items-start space-x-2">
                      <Footprints className="w-5 h-5 text-purple-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Accessibilité</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          <ClickableMetadataBadge 
                            category="difficulty"
                            value={spot.v2_data.accessibility.difficulty}
                            size="sm"
                          />
                          {spot.v2_data.accessibility.walk_time_minutes && (
                            <span className="text-sm text-gray-500 ml-2">
                              • {spot.v2_data.accessibility.walk_time_minutes} min de marche
                            </span>
                          )}
                        </div>
                        {spot.v2_data.accessibility.access_notes && (
                          <p className="mt-1 text-sm text-gray-500">{spot.v2_data.accessibility.access_notes}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fallback - Ancien format practical_info */}
                  {!spot.v2_data && spot.practical_info && (
                    <>
                      {spot.practical_info.recommended_hours && (
                        <div className="flex items-start space-x-2">
                          <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Heures recommandées</p>
                            <p className="text-sm text-gray-600">{spot.practical_info.recommended_hours}</p>
                          </div>
                        </div>
                      )}

                      {spot.practical_info.occupation_level && (
                        <div className="flex items-start space-x-2">
                          <Users className={`w-5 h-5 mt-0.5 ${getOccupationColor(spot.practical_info.occupation_level)}`} />
                          <div>
                            <p className="text-sm font-medium">Affluence</p>
                            <p className={`text-sm font-medium ${getOccupationColor(spot.practical_info.occupation_level)}`}>
                              {spot.practical_info.occupation_level}
                            </p>
                          </div>
                        </div>
                      )}

                      {spot.practical_info.accessibility && (
                        <div className="flex items-start space-x-2">
                          <Navigation className="w-5 h-5 text-purple-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Accessibilité</p>
                            <p className="text-sm text-gray-600">
                              {getAccessibilityIcon(spot.practical_info.accessibility)} {spot.practical_info.accessibility}
                            </p>
                          </div>
                        </div>
                      )}

                      {spot.practical_info.best_weather && (
                        <div className="flex items-start space-x-2">
                          <Cloud className="w-5 h-5 text-sky-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Météo idéale</p>
                            <p className="text-sm text-gray-600">{spot.practical_info.best_weather}</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {spot.tags && spot.tags.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {spot.tags.map((tagSlug, index) => {
                    const tag = state.allTags.find(t => t.slug === tagSlug);
                    const category = state.tagCategories.find(c => c.id === tag?.category_id);
                    
                    return (
                      <ClickableTag
                        key={index}
                        tag={tag || { slug: tagSlug, name: tagSlug }}
                        category={category}
                        size="sm"
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Share Section */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <Share2 className="w-5 h-5 mr-2" />
                Partager ce spot
              </h3>
              <div className="grid grid-cols-5 gap-2">
                <Button
                  onClick={() => handleShare('facebook')}
                  variant="outline"
                  size="sm"
                  className="flex flex-col items-center p-3 h-auto hover:bg-blue-50"
                >
                  <Facebook className="w-6 h-6 text-blue-600 mb-1" />
                  <span className="text-xs">Facebook</span>
                </Button>
                
                <Button
                  onClick={() => handleShare('twitter')}
                  variant="outline"
                  size="sm"
                  className="flex flex-col items-center p-3 h-auto hover:bg-sky-50"
                >
                  <Twitter className="w-6 h-6 text-sky-500 mb-1" />
                  <span className="text-xs">Twitter</span>
                </Button>
                
                <Button
                  onClick={() => handleShare('whatsapp')}
                  variant="outline"
                  size="sm"
                  className="flex flex-col items-center p-3 h-auto hover:bg-green-50"
                >
                  <MessageCircle className="w-6 h-6 text-green-500 mb-1" />
                  <span className="text-xs">WhatsApp</span>
                </Button>
                
                <Button
                  onClick={() => handleShare('email')}
                  variant="outline"
                  size="sm"
                  className="flex flex-col items-center p-3 h-auto hover:bg-gray-50"
                >
                  <Mail className="w-6 h-6 text-gray-500 mb-1" />
                  <span className="text-xs">Email</span>
                </Button>
                
                <Button
                  onClick={() => handleShare('copy')}
                  variant="outline"
                  size="sm"
                  className="flex flex-col items-center p-3 h-auto hover:bg-gray-50"
                >
                  <Copy className="w-6 h-6 text-gray-500 mb-1" />
                  <span className="text-xs">Copier</span>
                </Button>
              </div>
            </div>

            {/* Accessibility Section */}
            {hasValidParkingCoords() && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-green-600" />
                  Accessibilité
                </h3>
                
                <div className="space-y-4">
                  {/* Navigation buttons */}
                  <div className="flex space-x-3">
                    {!showGpsOptions ? (
                      // Initial state: single GPS button
                      <>
                        <Button
                          onClick={() => setShowGpsOptions(true)}
                          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Navigation className="w-4 h-4" />
                          <span>ITINÉRAIRE GPS</span>
                        </Button>
                        
                        {/* Parking photos button - always visible */}
                        <Button
                          onClick={() => setShowParkingPhotos(true)}
                          variant="outline"
                          className={`flex items-center justify-center w-10 h-10 ${
                            parkingPhotos.length > 0 
                              ? 'border-orange-200 hover:bg-orange-50 text-orange-700' 
                              : 'border-gray-200 hover:bg-gray-50 text-gray-400'
                          }`}
                          title={parkingPhotos.length > 0 ? 'Voir les photos du parking' : 'Aucune photo de parking disponible'}
                        >
                          <Camera className="w-5 h-5" />
                        </Button>
                      </>
                    ) : (
                      // Expanded state: show both destination options
                      <>
                        <Button
                          onClick={() => openGoogleMaps(spot.latitude, spot.longitude, 'spot')}
                          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>Aller au spot</span>
                        </Button>
                        
                        {hasValidParkingCoords() && (
                          <Button
                            onClick={() => openGoogleMaps(spot.parking_latitude, spot.parking_longitude, 'parking')}
                            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Car className="w-4 h-4" />
                            <span>Aller au parking</span>
                          </Button>
                        )}
                        
                        {/* Parking photos button - always visible */}
                        <Button
                          onClick={() => setShowParkingPhotos(true)}
                          variant="outline"
                          className={`flex items-center justify-center w-10 h-10 ${
                            parkingPhotos.length > 0 
                              ? 'border-orange-200 hover:bg-orange-50 text-orange-700' 
                              : 'border-gray-200 hover:bg-gray-50 text-gray-400'
                          }`}
                          title={parkingPhotos.length > 0 ? 'Voir les photos du parking' : 'Aucune photo de parking disponible'}
                        >
                          <Camera className="w-5 h-5" />
                        </Button>
                        
                        {/* Back button to collapse */}
                        <Button
                          onClick={() => setShowGpsOptions(false)}
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Interactive Satellite Minimap */}
                  {hasValidParkingCoords() && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 border-b">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Distance parking ↔ spot</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-semibold text-blue-600">
                              {(() => {
                                const distance = calculateDistance(spot.latitude, spot.longitude, spot.parking_latitude, spot.parking_longitude);
                                return distance < 1000 ? `${Math.round(distance)}m` : `${(distance/1000).toFixed(1)}km`;
                              })()}
                            </span>
                            <Button
                              onClick={() => setShowFullScreenMap(true)}
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                            >
                              <Expand className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="h-48 relative">
                        <MapContainer
                          center={[spot.latitude, spot.longitude]}
                          zoom={16}
                          style={{ height: '100%', width: '100%' }}
                          zoomControl={true}
                          attributionControl={true}
                          scrollWheelZoom={true}
                          dragging={true}
                          doubleClickZoom={true}
                        >
                          {/* Satellite Tile Layer */}
                          <TileLayer
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                          />
                          
                          {/* Spot marker */}
                          <Marker position={[spot.latitude, spot.longitude]}>
                            <Popup>
                              <div className="text-center">
                                <div className="font-semibold text-blue-600">📍 Spot</div>
                                <div className="text-sm">{spot.title}</div>
                              </div>
                            </Popup>
                          </Marker>
                          
                          {/* Parking marker with emoji */}
                          <Marker position={[spot.parking_latitude, spot.parking_longitude]} icon={createParkingIcon()}>
                            <Popup>
                              <div className="text-center">
                                <div className="font-semibold text-green-600">🅿️ Parking</div>
                                <div className="text-sm">{spot.practical_info.parking_info || 'Parking disponible'}</div>
                              </div>
                            </Popup>
                          </Marker>
                          
                          {/* Line connecting spot and parking */}
                          <Polyline
                            positions={[[spot.latitude, spot.longitude], [spot.parking_latitude, spot.parking_longitude]]}
                            color="#3B82F6"
                            weight={3}
                            opacity={0.8}
                            dashArray="5, 5"
                          />
                        </MapContainer>
                      </div>
                      
                      {/* Legend */}
                      <div className="bg-gray-50 px-3 py-2 border-t">
                        <div className="flex justify-between text-xs text-gray-600">
                          <span className="flex items-center">
                            <div className="w-3 h-3 bg-blue-600 rounded-full mr-1"></div>
                            📍 Spot
                          </span>
                          <span className="flex items-center">
                            <div className="w-3 h-3 bg-green-600 rounded-full mr-1"></div>
                            🅿️ Parking ({spot.practical_info.parking_distance})
                          </span>
                          <span className="text-xs text-gray-500 italic">Vue satellite • Interactif</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Parking info */}
                  {spot.practical_info.parking_info && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Car className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Information parking</p>
                          <p className="text-sm text-blue-700">{spot.practical_info.parking_info}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    {/* End of main Dialog */}
    </Dialog>

    {/* Parking Photos Modal - Rendered in portal to avoid Dialog conflicts */}
    {showParkingPhotos && typeof document !== 'undefined' && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 z-[99999] flex items-center justify-center"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => {
          // Close when clicking on the background (empty space)
          if (e.target === e.currentTarget) {
            setShowParkingPhotos(false);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation(); // Prevent closing the main modal
            setShowParkingPhotos(false);
          }
        }}
        tabIndex={0}
      >
        {/* Close button */}
        <button
          onClick={() => setShowParkingPhotos(false)}
          className="absolute top-4 right-4 z-[100000] bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
          style={{ pointerEvents: 'auto' }}
        >
          <X size={24} />
        </button>

        <div className="max-w-4xl max-h-[90vh] w-full mx-4">
          {/* Title */}
          <div className="text-center mb-4">
            <h2 className="text-white text-xl font-semibold">Photos du parking - {spot.title}</h2>
            {(spot.parking?.parking_info || spot.practical_info?.parking_info) && (
              <p className="text-gray-300 text-sm">{spot.parking?.parking_info || spot.practical_info?.parking_info}</p>
            )}
          </div>

          {/* Show photos if available, otherwise show message */}
          {parkingPhotos.length > 0 ? (
            <>
              {/* Main image container */}
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                <img
                  src={getParkingPhotoUrl(parkingPhotos[currentParkingImageIndex])}
                  alt={`Parking ${spot.title}`}
                  className="w-full h-auto max-h-[70vh] object-contain"
                />

            {/* Navigation arrows */}
            {parkingPhotos.length > 1 && (
              <>
                <button
                  onClick={() => navigateParkingImage('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all z-[100000]"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={() => navigateParkingImage('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all z-[100000]"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Image counter */}
            {parkingPhotos.length > 1 && (
              <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm">
                {currentParkingImageIndex + 1} / {parkingPhotos.length}
              </div>
            )}
          </div>

          {/* Image metadata */}
          {parkingPhotos[currentParkingImageIndex]?.metadata && (
            <div className="mt-4 bg-gray-800 rounded-lg p-4">
              <div className="text-white space-y-2">
                {parkingPhotos[currentParkingImageIndex].metadata.conditions && (
                  <div className="font-medium text-orange-300">
                    🅿️ {parkingPhotos[currentParkingImageIndex].metadata.conditions}
                  </div>
                )}
                <div className="text-sm text-gray-300 grid grid-cols-2 gap-2">
                  {parkingPhotos[currentParkingImageIndex].metadata.date && (
                    <span>📅 {parkingPhotos[currentParkingImageIndex].metadata.date}</span>
                  )}
                  {parkingPhotos[currentParkingImageIndex].metadata.time && (
                    <span>🕐 {parkingPhotos[currentParkingImageIndex].metadata.time}</span>
                  )}
                  {parkingPhotos[currentParkingImageIndex].metadata.season && (
                    <span>🌸 {parkingPhotos[currentParkingImageIndex].metadata.season}</span>
                  )}
                  {parkingPhotos[currentParkingImageIndex].metadata.weather && (
                    <span>🌤️ {parkingPhotos[currentParkingImageIndex].metadata.weather}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Thumbnails for multiple photos */}
          {parkingPhotos.length > 1 && (
            <div className="mt-4 flex justify-center space-x-2">
              {parkingPhotos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentParkingImageIndex(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentParkingImageIndex
                      ? 'border-orange-400 ring-2 ring-orange-300'
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <img
                    src={getParkingPhotoUrl(photo)}
                    alt={`Parking ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
            </>
          ) : (
            /* No parking photos available - show message */
            <div className="bg-gray-800 rounded-lg p-8 text-center">
              <Camera className="w-16 h-16 mx-auto text-gray-500 mb-4" />
              <h3 className="text-white text-lg font-medium mb-2">Aucune photo de parking disponible</h3>
              <p className="text-gray-400 text-sm">
                Ce spot n'a pas encore de photos de parking.
                <br />
                Si vous connaissez cet endroit, vous pouvez contribuer en ajoutant des photos lors de la création ou modification d'un spot.
              </p>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Full Screen Interactive Map Modal */}
    {showFullScreenMap && hasValidParkingCoords() && (
      <div 
        className="fixed inset-0 z-[99999]" 
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
        // Only block events that could trigger Radix Dialog close, allow mouseup/pointerup for Leaflet
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        // DON'T block mouseup/pointerup/touchend - Leaflet needs these to end drag operations
      >
        {/* Close button - ONLY way to close */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            console.log("Close button clicked");
            setShowFullScreenMap(false);
          }}
          className="fixed top-6 right-6 z-[100001] bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transition-all"
          style={{ pointerEvents: 'auto' }}
        >
          <X size={24} />
        </button>

        {/* Map title overlay - positioned to avoid zoom controls overlap */}
        <div 
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100001] bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg shadow-lg select-none"
          style={{ pointerEvents: 'none' }}
        >
          <h2 className="text-lg font-semibold text-center">{spot.title}</h2>
          <p className="text-sm opacity-90 text-center">
            Distance: {(() => {
              const distance = calculateDistance(spot.latitude, spot.longitude, spot.parking_latitude, spot.parking_longitude);
              return distance < 1000 ? `${Math.round(distance)}m` : `${(distance/1000).toFixed(1)}km`;
            })()}
          </p>
        </div>

        {/* Legend overlay */}
        <div 
          className="fixed bottom-6 left-6 z-[100001] bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg shadow-lg select-none"
          style={{ pointerEvents: 'none' }}
        >
          <div className="flex items-center space-x-4 text-sm">
            <span className="flex items-center">
              <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
              📍 Spot
            </span>
            <span className="flex items-center">
              <div className="w-3 h-3 bg-green-600 rounded-full mr-2"></div>
              🅿️ Parking
            </span>
          </div>
          <div className="text-xs opacity-75 mt-1">
            Molette: Zoom • Clic-glisser: Déplacer • Seule la croix rouge ferme
          </div>
        </div>

        {/* Full screen map container - Allow mouseup/pointerup for proper drag release */}
        <div 
          className="absolute inset-0 w-full h-full" 
          style={{ 
            pointerEvents: 'auto',
            zIndex: 99999
          }}
          // Block start events to prevent Radix Dialog close, allow end events for Leaflet
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          // Let mouseup, pointerup, touchend pass through to Leaflet
        >
          <MapContainer
            center={[spot.latitude, spot.longitude]}
            zoom={17}
            style={{ 
              height: '100vh', 
              width: '100vw',
              position: 'absolute',
              top: 0,
              left: 0
            }}
            zoomControl={true}
            attributionControl={false}
            scrollWheelZoom={true}
            dragging={true}
            doubleClickZoom={true}
            touchZoom={true}
            boxZoom={true}
            keyboard={true}
            preferCanvas={true}
            whenCreated={(map) => {
              // Only block events that could close Dialog, preserve drag release events
              const container = map.getContainer();
              container.addEventListener('pointerdown', (e) => e.stopPropagation(), true);
              container.addEventListener('mousedown', (e) => e.stopPropagation(), true);
              container.addEventListener('click', (e) => e.stopPropagation(), true);
              container.addEventListener('touchstart', (e) => e.stopPropagation(), true);
              // DON'T block mouseup, pointerup, touchend - Leaflet needs these for drag release
            }}
          >
            {/* Satellite Tile Layer */}
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='Tiles &copy; Esri'
            />
            
            {/* Transportation overlay */}
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
              opacity={0.6}
            />

            {/* Spot marker */}
            <Marker position={[spot.latitude, spot.longitude]}>
              <Popup>
                <div className="text-center min-w-[200px]">
                  <div className="font-semibold text-blue-600 text-lg mb-2">📍 {spot.title}</div>
                  <div className="text-sm text-gray-600 mb-3">{spot.description}</div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log("Spot navigation clicked");
                      openGoogleMaps(spot.latitude, spot.longitude, 'spot');
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    size="sm"
                  >
                    🧭 Navigation vers le spot
                  </Button>
                </div>
              </Popup>
            </Marker>

            {/* Parking marker */}
            <Marker position={[spot.parking_latitude, spot.parking_longitude]} icon={createParkingIcon()}>
              <Popup>
                <div className="text-center min-w-[200px]">
                  <div className="font-semibold text-green-600 text-lg mb-2">🅿️ Parking</div>
                  <div className="text-sm text-gray-600 mb-1">{spot.practical_info.parking_info || 'Parking disponible'}</div>
                  <div className="text-xs text-gray-500 mb-3">{spot.practical_info.parking_distance}</div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log("Parking navigation clicked");
                      openGoogleMaps(spot.parking_latitude, spot.parking_longitude, 'parking');
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                  >
                    🧭 Navigation vers parking
                  </Button>
                </div>
              </Popup>
            </Marker>

            {/* Connection line */}
            <Polyline
              positions={[[spot.latitude, spot.longitude], [spot.parking_latitude, spot.parking_longitude]]}
              color="#3B82F6"
              weight={4}
              opacity={0.8}
              dashArray="10, 10"
            />
          </MapContainer>
        </div>
      </div>
    )}
    
    {/* Add Visit Modal */}
    <AddVisitModal
      open={showAddVisitModal}
      onClose={() => setShowAddVisitModal(false)}
      spot={spot}
      onAddVisit={handleAddVisit}
    />
    </>
  );
}

export default SpotDetailsModal;